import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../api/apiClient';
import HorizontalFunnelChart from '../components/charts/HorizontalFunnelChart';
import LineTrendChart from '../components/charts/LineTrendChart';
import DonutChart from '../components/charts/DonutChart';
import BarChartCard from '../components/charts/BarChartCard';
import { CHART_COLORS } from '../components/charts/chartSetup';

const EMPTY_DASHBOARD = {
  summary: {
    adoption_rate: 0,
    active_users: 0,
    total_users: 0,
    total_courses: 0,
    total_enrollments: 0,
    completion_rate: 0,
    total_admins: 0,
    total_instructors: 0
  },
  users: { total: 0, active: 0, inactive: 0 },
  courses: { total: 0, active: 0, top_by_enrollment: [] },
  enrollments: { total: 0, trend: [] },
  licenses: { allocated: 0, used: 0, available: 0 },
  support_tickets: { total: 0, by_status: {}, open: 0, in_progress: 0, resolved: 0, closed: 0 },
  forums: { total: 0, total_comments: 0 },
  surveys: { total: 0, total_responses: 0 },
  vlt: { total_entries: 0, total_assignments: 0 },
  certificates: { templates: 0, issued: 0 },
  learning_funnel: [],
  active_users_trend: [],
  recent_activity: [],
  group_role_enrollment: {
    learner_groups: [],
    instructor_groups: []
  }
};

const ROLE_COUNTERS = [
  {
    key: 'total_admins',
    label: 'Org Admins',
    icon: 'fas fa-user-shield',
    color: 'text-purple-600',
    bg: 'from-purple-50 to-purple-100',
    path: '/organization/manage-admins'
  },
  {
    key: 'total_instructors',
    label: 'Org Instructors',
    icon: 'fas fa-chalkboard-teacher',
    color: 'text-indigo-600',
    bg: 'from-indigo-50 to-indigo-100',
    path: '/organization/manage-admins'
  },
  {
    key: 'total_users',
    label: 'Org Learners',
    icon: 'fas fa-user-graduate',
    color: 'text-blue-600',
    bg: 'from-blue-50 to-blue-100',
    path: '/organization/manage-users',
    showActive: true
  }
];

const ModuleSectionCard = ({ title, subtitle, icon, color, path, onNavigate, children }) => (
  <div className="bg-white rounded-2xl shadow-xl border border-purple-100 overflow-hidden h-full">
    <div className="flex items-center justify-between gap-3 p-5 border-b border-purple-50 bg-gradient-to-r from-white to-purple-50/30">
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-11 h-11 shrink-0 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shadow-md`}>
          <i className={`${icon} text-white text-lg`} aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-bold text-gray-900 truncate">{title}</h3>
          <p className="text-xs text-gray-500 truncate">{subtitle}</p>
        </div>
      </div>
      {path && (
        <button
          type="button"
          onClick={() => onNavigate(path)}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-purple-700 bg-purple-50 rounded-full hover:bg-purple-100 transition"
          aria-label={`Open ${title} module`}
        >
          Open
          <i className="fas fa-arrow-right text-[10px]" aria-hidden="true" />
        </button>
      )}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const formatRelativeTime = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';

  const diffMs = Date.now() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trendView, setTrendView] = useState('enrollments');
  const [dashboardData, setDashboardData] = useState(EMPTY_DASHBOARD);

  useEffect(() => {
    if (user?.organization_id) {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.dashboard.getOrgStats();
      if (response.data?.success) {
        setDashboardData(response.data.data || EMPTY_DASHBOARD);
      } else {
        throw new Error(response.data?.message || 'Failed to fetch dashboard data');
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const ticketLabels = useMemo(
    () => Object.keys(dashboardData.support_tickets.by_status || {}).map((status) => status.replace('_', ' ')),
    [dashboardData.support_tickets.by_status]
  );

  const ticketValues = useMemo(
    () => Object.values(dashboardData.support_tickets.by_status || {}),
    [dashboardData.support_tickets.by_status]
  );

  const topCourseLabels = useMemo(
    () => dashboardData.courses.top_by_enrollment.map((course) => course.course_title),
    [dashboardData.courses.top_by_enrollment]
  );

  const topCourseValues = useMemo(
    () => dashboardData.courses.top_by_enrollment.map((course) => course.enrollment_count),
    [dashboardData.courses.top_by_enrollment]
  );

  const licenseUtilization = dashboardData.licenses.allocated > 0
    ? Math.round((dashboardData.licenses.used / dashboardData.licenses.allocated) * 100)
    : 0;

  const openTickets = (dashboardData.support_tickets.open || 0)
    + (dashboardData.support_tickets.in_progress || 0);

  const secondaryMetrics = useMemo(() => ([
    {
      label: 'Courses',
      value: dashboardData.summary.total_courses,
      hint: `${dashboardData.courses.active} active`,
      icon: 'fas fa-graduation-cap',
      color: 'text-emerald-600',
      bg: 'from-emerald-50 to-emerald-100',
      path: '/organization/courses'
    },
    {
      label: 'Enrollments',
      value: dashboardData.summary.total_enrollments,
      hint: 'Total course seats',
      icon: 'fas fa-clipboard-list',
      color: 'text-teal-600',
      bg: 'from-teal-50 to-teal-100',
      path: '/organization/reports/users'
    },
    {
      label: 'Completion',
      value: `${dashboardData.summary.completion_rate}%`,
      hint: 'Learning outcomes',
      icon: 'fas fa-chart-line',
      color: 'text-violet-600',
      bg: 'from-violet-50 to-violet-100',
      path: '/organization/reports/users',
      raw: false
    },
    {
      label: 'Licenses Left',
      value: dashboardData.licenses.available,
      hint: `${dashboardData.licenses.used}/${dashboardData.licenses.allocated} used`,
      icon: 'fas fa-key',
      color: 'text-amber-600',
      bg: 'from-amber-50 to-amber-100',
      path: '/organization/manage-licenses'
    },
    {
      label: 'Open Tickets',
      value: openTickets,
      hint: `${dashboardData.support_tickets.total} total`,
      icon: 'fas fa-headset',
      color: 'text-rose-600',
      bg: 'from-rose-50 to-rose-100',
      path: '/organization/support-tickets'
    },
    {
      label: 'Certificates',
      value: dashboardData.certificates.issued,
      hint: `${dashboardData.certificates.templates} templates`,
      icon: 'fas fa-award',
      color: 'text-cyan-600',
      bg: 'from-cyan-50 to-cyan-100',
      path: '/organization/certificate-templates'
    }
  ]), [dashboardData, openTickets]);

  const learnerGroupData = dashboardData.group_role_enrollment?.learner_groups || [];
  const instructorGroupData = dashboardData.group_role_enrollment?.instructor_groups || [];

  const learnerGroupLabels = useMemo(
    () => learnerGroupData.map((group) => group.group_name),
    [learnerGroupData]
  );

  const learnerGroupEnrollments = useMemo(
    () => learnerGroupData.map((group) => group.total_enrollments),
    [learnerGroupData]
  );

  const instructorGroupLabels = useMemo(
    () => instructorGroupData.map((group) => group.group_name),
    [instructorGroupData]
  );

  const instructorGroupCounts = useMemo(
    () => instructorGroupData.map((group) => group.instructors),
    [instructorGroupData]
  );

  const totalLearnerGroupMembers = useMemo(
    () => learnerGroupData.reduce((sum, group) => sum + (group.members || 0), 0),
    [learnerGroupData]
  );

  const totalEnrolledInGroups = useMemo(
    () => learnerGroupData.reduce((sum, group) => sum + (group.enrolled_members || 0), 0),
    [learnerGroupData]
  );

  const totalInstructorsInGroups = useMemo(
    () => instructorGroupData.reduce((sum, group) => sum + (group.instructors || 0), 0),
    [instructorGroupData]
  );

  if (!user?.organization_id) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-600 border-t-transparent mx-auto" />
          <p className="mt-4 text-purple-800 font-semibold">Loading organization dashboard...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent mx-auto" />
          <p className="mt-4 text-purple-800 font-semibold">Fetching analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-100 p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-purple-100">
          <i className="fas fa-exclamation-circle text-5xl text-red-500 mb-4" aria-hidden="true" />
          <h4 className="text-xl font-bold text-gray-900 mb-2">Unable to load dashboard</h4>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            type="button"
            onClick={fetchDashboardData}
            className="inline-flex items-center px-6 py-3 bg-purple-600 text-white font-bold rounded-full hover:bg-purple-700 transition"
          >
            <i className="fas fa-redo mr-2" aria-hidden="true" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6 md:p-8">
      {/* Header */}
      <div className="mb-6 bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-purple-100 p-6 sm:p-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <i className="fas fa-tachometer-alt text-purple-600 text-2xl" aria-hidden="true" />
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Organization Dashboard</h1>
            </div>
            <p className="text-gray-600 text-sm sm:text-base">
              Welcome back, {user?.first_name || 'Admin'}! Overview of people, learning progress, and modules.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchDashboardData}
            className="inline-flex items-center px-5 py-2.5 bg-white border-2 border-purple-200 text-purple-700 font-semibold rounded-full hover:bg-purple-50 transition shadow-sm"
            aria-label="Refresh dashboard data"
          >
            <i className="fas fa-sync-alt mr-2" aria-hidden="true" />
            Refresh
          </button>
        </div>
      </div>

      {/* People KPIs */}
      <div className="mb-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3 px-1">People</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {ROLE_COUNTERS.map((counter) => (
            <button
              key={counter.key}
              type="button"
              onClick={() => navigate(counter.path)}
              className={`bg-gradient-to-br ${counter.bg} rounded-2xl border border-white shadow-lg p-5 text-left hover:scale-[1.02] transition-all`}
              aria-label={`View ${counter.label}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-xl bg-white/80 flex items-center justify-center ${counter.color}`}>
                  <i className={`${counter.icon} text-lg`} aria-hidden="true" />
                </div>
                <i className="fas fa-external-link-alt text-gray-400 text-xs" aria-hidden="true" />
              </div>
              <p className={`text-3xl font-bold ${counter.color}`}>
                {(dashboardData.summary[counter.key] ?? 0).toLocaleString()}
              </p>
              <p className="text-sm font-semibold text-gray-700 mt-1">{counter.label}</p>
              {counter.showActive && (
                <p className="text-xs text-emerald-600 font-medium mt-1">
                  {(dashboardData.summary.active_users ?? 0).toLocaleString()} active learners
                </p>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Learning & ops metrics */}
      <div className="mb-6">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3 px-1">Learning &amp; Operations</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {secondaryMetrics.map((metric) => (
            <button
              key={metric.label}
              type="button"
              onClick={() => navigate(metric.path)}
              className={`bg-gradient-to-br ${metric.bg} rounded-2xl border border-white shadow-md p-4 text-left hover:scale-[1.02] transition-all`}
              aria-label={`View ${metric.label}`}
            >
              <div className="flex items-center justify-between mb-2">
                <i className={`${metric.icon} ${metric.color}`} aria-hidden="true" />
              </div>
              <p className={`text-2xl font-bold ${metric.color}`}>
                {metric.raw === false
                  ? metric.value
                  : Number(metric.value || 0).toLocaleString()}
              </p>
              <p className="text-xs font-semibold text-gray-700 mt-1">{metric.label}</p>
              <p className="text-[11px] text-gray-500 mt-0.5 truncate">{metric.hint}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Engagement Trends */}
      <div className="bg-white rounded-2xl shadow-xl border border-purple-100 p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Engagement Trends</h2>
            <p className="text-sm text-gray-500">Last 14 days performance overview</p>
          </div>
          <div className="flex gap-2" role="tablist" aria-label="Trend chart filters">
            {[
              { key: 'enrollments', label: 'Enrollments' },
              { key: 'active_users', label: 'New Users' }
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={trendView === tab.key}
                onClick={() => setTrendView(tab.key)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                  trendView === tab.key
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        <LineTrendChart
          data={trendView === 'enrollments' ? dashboardData.enrollments.trend : dashboardData.active_users_trend}
          label={trendView === 'enrollments' ? 'Enrollments' : 'New Users'}
          color={trendView === 'enrollments' ? CHART_COLORS.green : CHART_COLORS.purple}
          yAxisLabel={trendView === 'enrollments' ? 'Enrollments' : 'Users'}
        />
      </div>

      {/* Progress overview */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        <ModuleSectionCard
          title="Learning Progress"
          subtitle="Enrollment to completion funnel"
          icon="fas fa-filter"
          color="from-violet-500 to-violet-700"
          path="/organization/reports/users"
          onNavigate={navigate}
        >
          <HorizontalFunnelChart
            stages={dashboardData.learning_funnel}
            title="Learning Progress Funnel"
          />
        </ModuleSectionCard>

        <ModuleSectionCard
          title="Learning Outcomes"
          subtitle="Completion and course activity"
          icon="fas fa-bullseye"
          color="from-pink-500 to-rose-600"
          path="/organization/reports/users"
          onNavigate={navigate}
        >
          <div className="space-y-5">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium text-gray-700">Completion Rate</span>
                <span className="font-bold text-purple-700">{dashboardData.summary.completion_rate}%</span>
              </div>
              <div className="w-full h-3 bg-purple-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-700"
                  style={{ width: `${dashboardData.summary.completion_rate}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium text-gray-700">Active Courses</span>
                <span className="font-bold text-blue-700">
                  {dashboardData.courses.active}/{dashboardData.courses.total}
                </span>
              </div>
              <div className="w-full h-3 bg-blue-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-700"
                  style={{
                    width: `${dashboardData.courses.total > 0 ? Math.round((dashboardData.courses.active / dashboardData.courses.total) * 100) : 0}%`
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="bg-purple-50 rounded-xl p-4 text-center border border-purple-100">
                <p className="text-2xl font-bold text-purple-700">
                  {(dashboardData.enrollments.total ?? 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-600 mt-1">Total Enrollments</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-4 text-center border border-emerald-100">
                <p className="text-2xl font-bold text-emerald-700">
                  {(dashboardData.vlt.total_assignments ?? 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-600 mt-1">VLT Assignments</p>
              </div>
            </div>
          </div>
        </ModuleSectionCard>
      </div>

      {/* Module analytics */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        <ModuleSectionCard
          title="Top Courses"
          subtitle="Highest enrollment courses"
          icon="fas fa-graduation-cap"
          color="from-emerald-500 to-emerald-700"
          path="/organization/courses"
          onNavigate={navigate}
        >
          <BarChartCard
            title="Top Courses by Enrollment"
            labels={topCourseLabels}
            values={topCourseValues}
            color={CHART_COLORS.green}
            horizontal
          />
        </ModuleSectionCard>

        <ModuleSectionCard
          title="Licenses"
          subtitle="Allocation and remaining seats"
          icon="fas fa-key"
          color="from-amber-500 to-amber-700"
          path="/organization/manage-licenses"
          onNavigate={navigate}
        >
          <DonutChart
            title="License Utilization"
            labels={['Used', 'Available']}
            values={[dashboardData.licenses.used, dashboardData.licenses.available]}
          />
          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 text-center">
            <div>
              <p className="text-lg font-bold text-gray-800">{dashboardData.licenses.allocated}</p>
              <p className="text-xs text-gray-500">Allocated</p>
            </div>
            <div>
              <p className="text-lg font-bold text-rose-600">{dashboardData.licenses.used}</p>
              <p className="text-xs text-gray-500">Used</p>
            </div>
            <div>
              <p className="text-lg font-bold text-emerald-600">{licenseUtilization}%</p>
              <p className="text-xs text-gray-500">Utilization</p>
            </div>
          </div>
        </ModuleSectionCard>

        <ModuleSectionCard
          title="Support Tickets"
          subtitle="Current ticket status mix"
          icon="fas fa-headset"
          color="from-rose-500 to-rose-700"
          path="/organization/support-tickets"
          onNavigate={navigate}
        >
          <DonutChart
            title="Support Tickets by Status"
            labels={ticketLabels}
            values={ticketValues}
          />
        </ModuleSectionCard>

        <ModuleSectionCard
          title="Module Engagement"
          subtitle="Forums, surveys, VLT and certificates"
          icon="fas fa-chart-bar"
          color="from-indigo-500 to-indigo-700"
          path="/organization/forums"
          onNavigate={navigate}
        >
          <BarChartCard
            title="Module Engagement Overview"
            labels={['Forums', 'Comments', 'Surveys', 'Responses', 'VLT', 'Certs']}
            values={[
              dashboardData.forums.total,
              dashboardData.forums.total_comments,
              dashboardData.surveys.total,
              dashboardData.surveys.total_responses,
              dashboardData.vlt.total_entries,
              dashboardData.certificates.issued
            ]}
            color={CHART_COLORS.purple}
          />
        </ModuleSectionCard>
      </div>

      {/* Group overview */}
      <div className="bg-white rounded-2xl shadow-xl border border-purple-100 p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <i className="fas fa-layer-group text-purple-600" aria-hidden="true" />
              Group Overview
            </h2>
            <p className="text-sm text-gray-500">Learner and instructor coverage across groups</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <div className="bg-purple-50 rounded-xl p-4 text-center border border-purple-100">
            <p className="text-2xl font-bold text-purple-700">{totalLearnerGroupMembers}</p>
            <p className="text-xs text-gray-600 mt-1">Learners in Groups</p>
          </div>
          <div className="bg-pink-50 rounded-xl p-4 text-center border border-pink-100">
            <p className="text-2xl font-bold text-pink-700">{totalEnrolledInGroups}</p>
            <p className="text-xs text-gray-600 mt-1">Enrolled in Groups</p>
          </div>
          <div className="bg-indigo-50 rounded-xl p-4 text-center border border-indigo-100">
            <p className="text-2xl font-bold text-indigo-700">{totalInstructorsInGroups}</p>
            <p className="text-xs text-gray-600 mt-1">Instructors in Groups</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <BarChartCard
              title="Learner Group Enrollments"
              labels={learnerGroupLabels}
              values={learnerGroupEnrollments}
              color={CHART_COLORS.green}
              horizontal
            />
            {learnerGroupData.length > 0 ? (
              <div className="mt-4 space-y-2 max-h-52 overflow-y-auto pr-1">
                {learnerGroupData.map((group) => (
                  <div
                    key={group.group_id}
                    className="flex items-center justify-between gap-3 p-3 rounded-lg bg-purple-50/60 border border-purple-100"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{group.group_name}</p>
                      <p className="text-xs text-gray-500">
                        {group.enrolled_members}/{group.members} learners enrolled
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="w-20 h-2 bg-purple-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                          style={{ width: `${group.enrollment_rate}%` }}
                        />
                      </div>
                      <span className="text-xs font-bold text-purple-700 w-8 text-right">{group.enrollment_rate}%</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No learner groups configured yet.</p>
            )}
          </div>

          <div>
            <BarChartCard
              title="Instructors per Group"
              labels={instructorGroupLabels}
              values={instructorGroupCounts}
              color={CHART_COLORS.purple}
              horizontal
            />
            {instructorGroupData.length > 0 ? (
              <div className="mt-4 space-y-2 max-h-52 overflow-y-auto pr-1">
                {instructorGroupData.map((group) => (
                  <div
                    key={group.group_id}
                    className="flex items-center justify-between gap-3 p-3 rounded-lg bg-indigo-50/60 border border-indigo-100"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{group.group_name}</p>
                      <p className="text-xs text-gray-500">
                        {group.instructors} instructor{group.instructors === 1 ? '' : 's'} assigned
                      </p>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700 shrink-0">
                      {group.linked_courses} course{group.linked_courses === 1 ? '' : 's'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No instructor groups configured yet.</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div className="bg-white rounded-2xl shadow-xl border border-purple-100 overflow-hidden">
        <div className="p-6 border-b border-purple-100 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Recent Learning Activity</h3>
            <p className="text-sm text-gray-500">Latest learner course progress in your organization</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/organization/reports/course-completions')}
            className="shrink-0 text-sm font-semibold text-purple-600 hover:text-purple-800"
          >
            View Reports
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 uppercase bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">User</th>
                <th className="px-6 py-3 text-left">Action</th>
                <th className="px-6 py-3 text-left">Course</th>
                <th className="px-6 py-3 text-left">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {dashboardData.recent_activity.length > 0 ? (
                dashboardData.recent_activity.map((activity, index) => (
                  <tr key={`${activity.user_name}-${index}`} className="hover:bg-purple-50 transition">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white text-xs font-bold">
                          {(activity.user_name || 'U')[0]}
                        </div>
                        <span className="font-medium text-gray-900">{activity.user_name || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
                          activity.action === 'Completed'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-cyan-100 text-cyan-700'
                        }`}
                      >
                        {activity.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-900">{activity.course_name}</td>
                    <td className="px-6 py-4 text-gray-500">{formatRelativeTime(activity.activity_at)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center text-gray-500">
                    No recent activity yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
